import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, Monitor, Smartphone, MessageCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import PriceTable from './PriceTable';
import { BUSINESS, type PriceItem } from '../config/business';

import TrackedContactLink from './TrackedContactLink';
interface PricingColumn {
  title: string;
  category: PriceItem['category'];
  icon: LucideIcon;
}

const COLUMNS: PricingColumn[] = [
  { title: 'Réparation téléphone', category: 'telephone', icon: Smartphone },
  { title: 'Informatique', category: 'informatique', icon: Monitor },
  { title: 'Sites web', category: 'web', icon: Globe },
];

const PricingSection: React.FC = () => (
  <section id="tarifs" className="relative bg-[#36393F] py-16 overflow-hidden">
    <div className="absolute top-[-60px] right-[10%] w-64 h-64 bg-[#5865F2]/10 rounded-full blur-3xl pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        className="text-center mb-10"
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
      >
        <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
          Nos <span className="text-[#5865F2]">tarifs</span>
        </h2>
        <p className="text-gray-300 max-w-2xl mx-auto">
          Tarifs à partir de, devis gratuit et rapide. Pièces de qualité, {BUSINESS.warrantyLabel}.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {COLUMNS.map(({ title, category, icon: Icon }, index) => (
          <motion.div
            key={category}
            className="rounded-2xl border border-white/10 bg-[#2F3136] p-5"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.45, delay: index * 0.08, ease: 'easeOut' }}
          >
            <h3 className="flex items-center gap-2 text-white font-semibold mb-4">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[#5865F2]/20 text-[#9BA8FF]">
                <Icon size={18} aria-hidden="true" />
              </span>
              {title}
            </h3>
            <PriceTable categories={[category]} caption={`Tarifs ${title}`} />
          </motion.div>
        ))}
      </div>

      <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
        <TrackedContactLink channel="whatsapp" service="autre" ctaLocation="pricing" 
          href={BUSINESS.whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-6 py-3 bg-[#5865F2] hover:bg-[#4752C4] text-white font-medium rounded-lg transition-colors"
        >
          <MessageCircle className="mr-2 h-4 w-4" aria-hidden="true" />
          Demander un devis sur WhatsApp
        </TrackedContactLink>
        <Link
          to="/reparation-telephone#devis"
          className="inline-flex items-center px-6 py-3 bg-white/5 border border-white/10 text-white font-medium rounded-lg hover:bg-white/10 transition-colors"
        >
          Simulateur de prix téléphone
          <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </div>
  </section>
);

export default PricingSection;
